import { useQueryStates } from 'nuqs'
import { useSearchParams } from 'react-router'
import { searchParamsParsers, serializeSearchParams } from './search-params'

/**
 * Hook that returns a function to build URLs with preserved nuqs-managed search parameters
 * This preserves only the params that are explicitly present in the URL (not defaults)
 */
export function usePreserveNuqsParams(paramsToExclude?: string[]) {
	const [queryStates] = useQueryStates(searchParamsParsers)
	const [searchParams] = useSearchParams()

	/**
	 * Builds a URL with preserved nuqs parameters
	 * @param path - The path to navigate to (can use React Router's href() for type-safe paths)
	 * @param overrides - Optional parameters to override or add
	 * @returns The full URL with preserved search parameters
	 */
	const buildUrl = (path: string, overrides?: Partial<typeof queryStates>) => {
		// Filter to only params explicitly in URL (not defaults)
		const explicitParams: Partial<typeof queryStates> = {}
		for (const [key, value] of Object.entries(queryStates)) {
			if (paramsToExclude?.includes(key)) continue
			if (searchParams.has(key) && value !== null && value !== undefined) {
				;(explicitParams as Record<string, unknown>)[key] = value
			}
		}

		// Merge with overrides (null values will be removed by serializer)
		const finalParams = { ...explicitParams, ...overrides }

		return serializeSearchParams(path, finalParams)
	}

	return buildUrl
}
